import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import UserService from 'services/userService';
import ProductService from 'services/productService';
import OrderService from 'services/orderService';
import TicketService from 'services/ticketService';
import Modal from 'components/Modal';
import ProductCard from 'components/ProductCard';
import UserLayout from 'layouts/UserLayout';

async function productsLoader() {
  if (sessionStorage.getItem('products')) {
    return JSON.parse(sessionStorage.getItem('products'));
  }
  return await ProductService.getProducts();
}

export default function HomePage() {
  const [currentUser, setCurrentUser] = useState(UserService.getCurrentUser());
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [note, setNote] = useState('');
  const [product, setProduct] = useState(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  const closeOrderModal = () => {
    setOrderModalOpen(false);
  };

  const closeTicketModal = () => {
    setTicketModalOpen(false);
  };

  const handleCheckout = async () => {
    OrderService.createOrder({ note: note, product: product }).then((response) => {
      if (response.status === 'Success') {
        toast.success(response.message, { description: 'Check your order in the order page' });
        updateUserBalance(response.balance);
        closeOrderModal();
      }
    });
  };

  const updateUserBalance = (balance) => {
    const updatedUser = { ...currentUser, balance: balance };
    setCurrentUser(updatedUser);
    // Update localStorage to persist the balance change
    UserService.updateLocalBalance(balance);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('userBalanceUpdated'));
  };

  const handleSubmitTicket = () => {
    if (subject === '' || description === '') {
      toast.error('Please fill the subject and description');
      return;
    }

    TicketService.createTicket({ subject: subject, description: description }).then((response) => {
      if (response.status === 'Success') {
        toast.success(response.message, { description: 'We will response as soon as possible (~1 minute)' });
        closeTicketModal();
      }
    });
  };

  useEffect(() => {
    productsLoader().then((products) => setProducts(products));
  }, []);

  return (
    <UserLayout currentUser={currentUser}>
      <Modal id="order-modal" isOpen={orderModalOpen}>
        <div className="flex flex-col items-center justify-center p-8">
          <h1 className="text-3xl text-white font-bold">Confirm Purchase</h1>
          <div className="w-full mt-12">
            <textarea
              className="w-full p-4 h-48 bg-[#292929] text-white rounded-xl focus:outline-none focus:shadow-outline resize-none"
              placeholder="Add your note here"
              onChange={(event) => setNote(event.target.value)}
            ></textarea>
          </div>
          <p className="text-white mt-4 select-none">Are you sure to purchase this product ?</p>
          <div className="flex flex-col mt-3 gap-2 w-full">
            <button
              type="button"
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline"
              onClick={handleCheckout}
            >
              Confirm
            </button>
            <button
              type="button"
              className="w-full bg-[#292929] hover:bg-[#313131] text-gray-500 font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline"
              onClick={closeOrderModal}
            >
              Cancel
            </button>
            {/* <p className="text-center text-xs text-indigo-300 italic select-none">This is gonna take a while</p> */}
          </div>
        </div>
      </Modal>
      <Modal id="ticket-modal" isOpen={ticketModalOpen}>
        <div className="flex flex-col items-center justify-center p-8">
          <h1 className="text-3xl text-white font-bold">Support Ticket</h1>
          <p className="text-white text-center mt-4 select-none">What can we help you ?</p>

          <div className="w-full mt-8">
            <textarea
              className="w-full p-4 h-14 bg-[#292929] text-white rounded-xl focus:outline-none focus:shadow-outline resize-none"
              maxLength={32}
              placeholder="Ticket's Subject"
              onChange={(event) => setSubject(event.target.value)}
            ></textarea>
            <textarea
              className="w-full p-4 h-48 bg-[#292929] text-white rounded-xl focus:outline-none focus:shadow-outline resize-none"
              placeholder="Ticket's Description"
              onChange={(event) => setDescription(event.target.value)}
            ></textarea>
          </div>
          <div className="flex flex-col mt-3 gap-2 w-full">
            <button
              type="button"
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline"
              onClick={handleSubmitTicket}
            >
              Confirm
            </button>
            <button
              type="button"
              className="w-full bg-[#292929] hover:bg-[#313131] text-gray-500 font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline"
              onClick={closeTicketModal}
            >
              Cancel
            </button>
            {/* <p className="text-center text-xs text-indigo-300 italic select-none">This is gonna take a while</p> */}
          </div>
        </div>
      </Modal>
      {products.length === 0 ? (
        <p className="text-white text-center">No products available</p>
      ) : (
        <div className="h-[85vh] overflow-y-scroll scrollbar-thin p-2">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} setProduct={setProduct} setModalOpen={setOrderModalOpen} />
            ))}
          </div>
        </div>
      )}
      <div
        className="
          fixed bottom-0 right-0 
          m-12 w-16 h-16
          rounded-full bg-indigo-500 
          hover:scale-[1.1] transform transition duration-300 
          select-none cursor-pointer 
          flex items-center justify-center
        "
        onClick={() => setTicketModalOpen(true)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8 ">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
          />
        </svg>
      </div>
    </UserLayout>
  );
}
